/**
 * Плеер озвучки · Ф-D — единственный «звуковой мозг» витрины.
 *
 * Что здесь есть и почему именно здесь:
 *
 * ⭐ ЗАКОН. Звук доступен ТОЛЬКО когда английское слово уже на экране. Пока слово
 *    скрыто (окрепшее слово, вопрос по-русски), кнопка звука выдала бы ответ —
 *    поэтому её не существует, а не «она есть, но не работает». Решение владельца
 *    25.07 по макету `design/audio-button-variants.html`.
 *
 * • ОДИН объект `Audio` на всё приложение: два слова не должны заговорить разом,
 *   а «предыдущее ещё играет» лечится само — новый запуск гасит старый.
 * • ⚠ `<audio src="/api/audio/word/1">` НЕ ШЛЁТ заголовок с подписью Telegram, а API
 *   проверяет подпись на каждом запросе (закон 2.1, папка статикой не открыта).
 *   Поэтому файл тянем `fetch()`-ем с заголовком и отдаём плееру `blob:`-адрес.
 *   Адреса живут в маленьком кеше и ОТЗЫВАЮТСЯ (`URL.revokeObjectURL`) — иначе за
 *   занятие в памяти осядет десяток мегабайт mp3.
 * • «Медленная» скорость = `playbackRate 0.7` + `preservesPitch` (26.07; 0.85 → 0.7 по замеру ушами 31.07:
 *   владелец сравнил с честной переозвучкой −15% и сказал «в плеере нет разницы»).
 *   Второго набора файлов нет — ретроактивно работает на всём уже озвученном.
 * • ⚠ Автозапуск браузеры и Telegram WebView глушат до первого касания. Первое
 *   нажатие «Учить» считаем разрешением (`unlock()`); отказали — честно помечаем
 *   `blocked`, и кнопка остаётся ручной, а не молчит без объяснения.
 * • ⛔ Выключил звук — ни одного автозапуска и ни одной кнопки.
 *
 * Сеть внедряется снаружи (`setLoader`) — так у файла нет ни одного рантайм-импорта,
 * и его чистые функции гоняются самопроверкой в node: `node tools/check-audio.mjs`.
 */

import type { Card } from "./api";

/* ── настройки (пер-юзерные строки `settings`, дефолт подставляет API) ─────── */

export type AudioMode = "off" | "word" | "word_phrase";
export type AudioSpeed = "normal" | "slow";

export interface AudioPrefs {
  /** что вообще звучит */
  mode: AudioMode;
  /** проговаривать новое слово само, как только оно появилось на экране */
  autoplay: boolean;
  speed: AudioSpeed;
}

/**
 * Дефолты держим и здесь тоже: витрина обязана работать, даже если API их не прислал.
 * ⭐ 26.07: по умолчанию звучат СЛОВО И ФРАЗА (решение владельца: «пусть выключают сами») —
 * должно совпадать с `UI_DEFAULTS` в `core/api.py`.
 */
export const DEFAULT_PREFS: AudioPrefs = { mode: "word_phrase", autoplay: true, speed: "normal" };

/**
 * Замедление плеером.
 * ⚠ ЗАМЕР УШАМИ 31.07 (владелец, боевое приложение): **0.85 не читается как «медленно»** —
 * 15 % разницы на слух не слышно, и человек решил, что функции просто нет. Ставим 0.7:
 * речь ощутимо разбирается по словам, а `preservesPitch` держит голос на месте (без него
 * получается «бурундук наоборот»). Прошлое значение выбиралось сравнением с ПЕРЕОЗВУЧКОЙ,
 * а вопрос был другой — слышно ли разницу С ОБЫЧНЫМ ТЕМПОМ.
 */
export const SLOW_RATE = 0.7;

const MODES: AudioMode[] = ["off", "word", "word_phrase"];
const SPEEDS: AudioSpeed[] = ["normal", "slow"];

/**
 * Настройки из ответа API. ⚠ `audio_autoplay` приходит то числом (дефолт), то строкой
 * «0»/«1» (после первой правки: ядро приводит к int только свои ключи) — принимаем оба.
 */
export function readPrefs(settings: Record<string, unknown> | null | undefined): AudioPrefs {
  const s = settings || {};
  const mode = MODES.find((m) => m === s.audio_mode) ?? DEFAULT_PREFS.mode;
  const speed = SPEEDS.find((v) => v === s.audio_speed) ?? DEFAULT_PREFS.speed;
  const raw = s.audio_autoplay;
  const autoplay = raw === undefined || raw === null || raw === ""
    ? DEFAULT_PREFS.autoplay
    : !(raw === 0 || raw === "0" || raw === false);
  return { mode, autoplay, speed };
}

/** Обратно в строки настроек — тем же ключом, что принимает `PATCH /settings`. */
export function prefsToSettings(p: AudioPrefs): Record<string, string | number> {
  return { audio_mode: p.mode, audio_autoplay: p.autoplay ? 1 : 0, audio_speed: p.speed };
}

export function rateFor(speed: AudioSpeed): number {
  return speed === "slow" ? SLOW_RATE : 1;
}

/* ── ⭐⭐ СКОРОСТЬ ЧЕРЕДОВАНИЕМ (окно 1 запуска, 31.07) ─────────────────────── */

/**
 * Идея владельца из обкатки ступени «На слух»: не разобрал слово — хочу переслушать
 * МЕДЛЕННО, не уходя в настройки. Второго набора файлов для этого не нужно — замедляет
 * плеер (`playbackRate` 0.7 + `preservesPitch`), он это умеет с 26.07.
 *
 * Правила, и все три проверяются запуском (`tools/check-audio.mjs`):
 *   1. ⭐ настройка «Скорость» ГЛАВНЕЕ: выбрана «Медленная» — звучит медленно всегда,
 *      чередование с ней не спорит и не «ускоряет» через раз;
 *   2. при «Стандартной» первое проигрывание записи обычное, повторное — медленное,
 *      дальше чередование. ⭐ Считаем именно ПРОИГРЫВАНИЯ, а не нажатия: если слово
 *      проговорилось само (автозапуск), то нажатие на кнопку — уже «переслушать», и
 *      человек нажимает ровно потому, что не расслышал;
 *   3. у каждой записи свой счёт (слово и фраза чередуются независимо — это разные
 *      кнопки), и он обнуляется на новой карточке: чередование живёт внутри карточки,
 *      а не тянется через занятие.
 *
 * ⛔ Звук выключен — правил нет вовсе: кнопки не существует, играть нечего.
 */
const plays = new Map<string, number>();

/** Следующее проигрывание этой записи пойдёт МЕДЛЕННО? Этим же красится иконка кнопки. */
export function slowNext(r: string | null, p: AudioPrefs = prefs()): boolean {
  if (!r || p.mode === "off") return false;
  if (p.speed === "slow") return true;          // настройка главнее чередования
  // ⭐⭐ ЧЕРЕДОВАНИЕ — закон владельца, подтверждённый дважды (окно 1 и повторно 31.07):
  // обычно → медленно → обычно → медленно. ⛔ «Все повторы медленные» пробовали 31.07 и
  // ОТКАТИЛИ по его же слову: он хочет слышать оба темпа подряд, а не уезжать в медленный
  // до конца карточки. Видно это теперь значком `0.7×` на кнопке — он гаснет и загорается
  // вместе с чередованием.
  return (plays.get(r) || 0) % 2 === 1;
}

/** Скорость, с которой запись прозвучит ПРЯМО СЕЙЧАС. */
export function speedForNext(r: string | null, p: AudioPrefs = prefs()): AudioSpeed {
  return slowNext(r, p) ? "slow" : "normal";
}

/** Запись прозвучала (нажатием или сама) — двигаем чередование. */
export function notePlayed(r: string | null): void {
  if (!r) return;
  plays.set(r, (plays.get(r) || 0) + 1);
}

/** Новая карточка: чередование начинается заново. Сколько раз играли — забываем. */
export function resetSpeedCycle(): void {
  plays.clear();
}

/* ── что можно озвучить прямо сейчас ──────────────────────────────────────── */

export type Kind = "word" | "phrase";
export type CardMode = "silence" | "dossier" | "choice" | "context" | "build" | "listen" | "gap"
                     | "hear" | "fluency";

/** Куда указывает кнопка: «word:17» — слово lexicon_id 17, «phrase:42» — пример 42. */
export function ref(kind: Kind, id: number): string {
  return `${kind}:${id}`;
}

export function parseRef(r: string): { kind: Kind; id: number } | null {
  const [kind, raw] = String(r || "").split(":");
  const id = Number(raw);
  if ((kind !== "word" && kind !== "phrase") || !Number.isFinite(id) || id <= 0) return null;
  return { kind, id };
}

/** ⭐ Английское слово на экране? Вопрос EN→RU — да сразу; RU→EN — только после раскрытия. */
export function englishOnScreen(c: Card, open: boolean): boolean {
  return open || c.direction === "en_ru";
}

export interface Places {
  /** адрес озвучки слова, если кнопке слова есть где стоять; иначе null */
  word: string | null;
  /** адрес озвучки фразы-примера: везде, где пример УЖЕ раскрыт (правка 26.07) */
  phrase: string | null;
  /**
   * ⭐ М5: фраза здесь — САМ ВОПРОС (ступень 5 «фраза на слух»), а не добавка к ответу.
   * Отсюда два следствия: её слышно ДО ответа и её же проговаривает автозапуск.
   */
  ask?: boolean;
}

/**
 * Где в этом виде карточки и на этом шаге живут кнопки звука.
 * Места — ровно те, что выбрал владелец 25.07 по макету:
 *   • Т2 «Тишина»  — волна под словом (вопрос английский) → доступно сразу;
 *   • Д1 «Досье»   — иконка в строке с транскрипцией → доступно сразу;
 *   • В2 «Выбор»   — ⛔ до ответа звука нет (иначе это подсказка), после — у ответа;
 *   • К1 «Контекст» — ⛔ до сверки звука нет (фраза содержит ответ), после — слово и фраза.
 * Поверх всего — ⭐ ЗАКОН `englishOnScreen` и настройка «Звук».
 *
 * ⭐ «Собери фразу» (окно 3, 26.07) идёт по ОБЩЕМУ закону, без своего исключения:
 * у нового слова карточка показывает лемму дозой ДО сборки (`go` с переводом), то есть
 * английское слово уже на экране — кнопка слова живёт там же, где в «Досье». У окрепшего
 * слова леммы на экране нет, значит и кнопки нет до разбора. Фраза, как везде, звучит
 * только после сверки: в ней стоит собранный ответ целиком.
 *
 * ⭐ Правка 26.07 (живая обкатка: «кнопку „Фраза“ не нашёл»). Фраза звучит ВЕЗДЕ, где
 * пример уже раскрыт, а не только в «Контексте»: пример виден в трёх видах из четырёх,
 * и кнопку ищут там же. Условие простое и общее — ответ показан, значит выдавать нечего.
 * До раскрытия фразы нет нигде и никогда: в ней стоит то самое слово.
 *
 * ⭐⭐ ОКНО М2 · ЗАКОН РАЗВОРАЧИВАЕТСЯ НА «НА СЛУХ» (`listen`, ступень 1а). Во всех
 * остальных видах порядок такой: английское слово на экране → значит можно и звук
 * (кнопка ничего не выдаёт). Здесь наоборот: **звук — это САМ ВОПРОС**, поэтому текста
 * на экране НЕТ до ответа, а кнопка «Прослушать» доступна сразу и она обязательна —
 * без неё карточка просто не задаёт вопроса. Формально закон не нарушен, а прочитан с
 * другой стороны: выдать ответ кнопка не может (ответ здесь — РУССКИЙ перевод, его
 * человек и выбирает), а написание слова появляется после ответа, вместе с разбором.
 * Фраза, как везде, только после ответа: в ней стоит то самое слово.
 *
 * ⭐⭐ ОКНО М5 · ТО ЖЕ САМОЕ, НО ФРАЗОЙ (`hear`, ступень 5). Здесь вопрос задан ЦЕЛЫМ
 * ПРЕДЛОЖЕНИЕМ, поэтому доступна до ответа именно фраза — и это не дыра в законе, а он
 * же: на экране предложение стоит С ПРОПУСКОМ, слова в нём нет, выдавать нечем. А вот
 * кнопка СЛОВА здесь молчит до ответа (в отличие от ступени 1а, где слово и было
 * вопросом): слово стоит на одной из кнопок, и озвучить его = сказать ответ вслух.
 * ⚠ Отсюда же следует, что режим звука «Слово» (без фраз) ступень 5 не собирает вовсе —
 * задавать вопрос было бы нечем, и лестница молча отдаёт ступень 4.
 *
 * ⭐⭐ ОКНО М6 · СТУПЕНЬ 6 «БЕГЛОСТЬ» (`fluency`) — общий закон плюс ОДНО сужение: кнопки
 * «Фраза» здесь нет ни до, ни после ответа, потому что примера на карточке нет вовсе
 * (разбор урезан до трёх строк). Слово живёт по общему правилу: на боксе 6 вопрос задан
 * по-русски, значит до раскрытия английского слова на экране нет — и кнопки слова тоже.
 */
export function places(c: Card, mode: CardMode, open: boolean, p: AudioPrefs = prefs()): Places {
  if (p.mode === "off") return { word: null, phrase: null };
  // ⭐ М3: «Пропуск» (ступень 3) — по тому же закону, что «Выбор» и «Контекст»: до ответа
  // звука нет. Слово стоит на одной из кнопок, и озвучка выдала бы ответ вслух.
  // ⭐ М5: «Фраза на слух» (ступень 5) — там же: слово тоже стоит на кнопке, поэтому
  // озвучка СЛОВА молчит до ответа. А вот ФРАЗА, наоборот, доступна сразу — она и есть
  // вопрос (в ней на месте слова пропуск, выдавать нечем).
  const afterAnswerOnly = mode === "choice" || mode === "context" || mode === "gap"
                        || mode === "hear";
  const wordOk = englishOnScreen(c, open) && (!afterAnswerOnly || open);
  const ask = mode === "hear" && !open && p.mode === "word_phrase" && !!c.example_id;
  // ⭐⭐ М6 · НА СТУПЕНИ 6 ПРИМЕРА НА ЭКРАНЕ НЕТ ВОВСЕ (разбор урезан до трёх строк:
  // слово · перевод · звук слова), значит и кнопке «Фраза» стоять негде. Это не запрет
  // ради запрета: ступень меряет ТЕМП, а восемь блоков между карточками ломают ровно то,
  // что мы меряем. Слово при этом озвучивается по общему закону — после раскрытия.
  const noPhrase = mode === "fluency";
  return {
    word: wordOk && c.lexicon_id ? ref("word", c.lexicon_id) : null,
    phrase: !noPhrase && p.mode === "word_phrase" && (open || ask) && c.example_id
      ? ref("phrase", c.example_id)
      : null,
    ask,
  };
}

/**
 * Что проговорить само. Нечего — null: ⛔ звук выключен, ⛔ «само» выключено,
 * ⛔ браузер отказал (тогда кнопка честно ручная), ⛔ слова на экране ещё нет.
 *
 * ⭐ М5: на ступени 5 сам собой звучит ПРИМЕР, а не слово, — потому что там вопрос
 * задан примером. Это то же правило, что и всегда («проговариваем вопрос»), просто на
 * этой карточке вопросом работает фраза.
 */
export function autoplayRef(pl: Places, p: AudioPrefs = prefs(), refused = blocked): string | null {
  if (p.mode === "off" || !p.autoplay || refused) return null;
  return pl.word ?? (pl.ask ? pl.phrase : null);
}

/** Файлы следующей карточки: тянем заранее, чтобы звук шёл без паузы на загрузку. */
export function preloadRefs(c: Card, p: AudioPrefs = prefs()): string[] {
  if (p.mode === "off") return [];
  const out: string[] = [];
  if (c.lexicon_id) out.push(ref("word", c.lexicon_id));
  if (p.mode === "word_phrase" && c.example_id) out.push(ref("phrase", c.example_id));
  return out;
}

/* ── движок: один Audio, кеш blob-адресов, состояния кнопок ───────────────── */

export type Loader = (kind: Kind, id: number) => Promise<Blob>;

/** Сколько записей держим в памяти. Слово ≈ 10 КБ, фраза ≈ 15 КБ → потолок ≈ 250 КБ. */
const CACHE_MAX = 16;

/** Пустой звук на «разрешение от первого касания»: 8 тихих сэмплов, 8 кГц, моно. */
const SILENCE =
  "data:audio/wav;base64,UklGRiwAAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQgAAACAgICAgICAgA==";

let current: AudioPrefs = { ...DEFAULT_PREFS };
let loader: Loader | null = null;
let el: HTMLAudioElement | null = null;

let playing: string | null = null;   // что звучит прямо сейчас
let loading: string | null = null;   // что тянется из сети
let token = 0;                       // счётчик запусков: поздний ответ сети не перебьёт новый

const urls = new Map<string, string>();            // ref → blob:-адрес (порядок вставки = возраст)
const inflight = new Map<string, Promise<string | null>>();   // чтобы один файл не тянуть дважды
const gone = new Set<string>();                    // ref → «озвучки нет» (404), больше не просим

let unlocked = false;
let blocked = false;

export function setLoader(fn: Loader): void {
  loader = fn;
}

export function prefs(): AudioPrefs {
  return current;
}

/** Настройки сменились: скорость применяем сразу — даже к записи, которая уже играет. */
export function setPrefs(next: AudioPrefs): void {
  current = { ...next };
  if (el) el.playbackRate = rateFor(current.speed);
  if (current.mode === "off") stop();
  sync();
}

export function autoplayBlocked(): boolean {
  return blocked;
}

function applyPitch(a: HTMLAudioElement, speed: AudioSpeed = current.speed): void {
  // Без сохранения высоты замедленный голос уезжает вверх («бурундук») — на старых
  // WebView свойство ещё вендорное, поэтому ставим все три.
  const any = a as HTMLAudioElement & {
    preservesPitch?: boolean; mozPreservesPitch?: boolean; webkitPreservesPitch?: boolean;
  };
  any.preservesPitch = true;
  any.mozPreservesPitch = true;
  any.webkitPreservesPitch = true;
  a.playbackRate = rateFor(speed);
}

function ensureEl(): HTMLAudioElement {
  if (el) return el;
  el = new Audio();
  el.preload = "auto";
  el.addEventListener("ended", () => { playing = null; sync(); });
  el.addEventListener("pause", () => { playing = null; sync(); });
  el.addEventListener("error", () => { playing = null; sync(); });
  applyPitch(el);
  return el;
}

/** Первое касание = разрешение на звук. Отказали — помним и не давим автозапуском. */
export function unlock(): void {
  if (unlocked || typeof Audio === "undefined") return;
  const a = ensureEl();
  a.muted = true;
  a.src = SILENCE;
  const done = (): void => { a.muted = false; };
  const started = a.play();
  if (!started || typeof started.then !== "function") { unlocked = true; done(); return; }
  void started
    .then(() => { unlocked = true; a.pause(); a.currentTime = 0; })
    .catch(() => { blocked = true; })
    .finally(done);
}

function remember(r: string, url: string): void {
  const old = urls.get(r);
  if (old && old !== url) URL.revokeObjectURL(old);   // тот же файл переехал — старый адрес мёртв
  urls.set(r, url);
  while (urls.size > CACHE_MAX) {
    const oldest = [...urls.keys()].find((k) => k !== playing && k !== r);
    if (!oldest) break;
    const dead = urls.get(oldest);
    urls.delete(oldest);
    if (dead) URL.revokeObjectURL(dead);
  }
}

async function grab(r: string): Promise<string | null> {
  const cached = urls.get(r);
  if (cached) return cached;
  const p = parseRef(r);
  if (!p || !loader || gone.has(r)) return null;
  // Один и тот же файл могут попросить сразу двое (нажали кнопку, пока шла предзагрузка) —
  // тянем ОДИН раз, второй ждёт тот же ответ. Иначе и сеть дважды, и лишний blob в памяти.
  const already = inflight.get(r);
  if (already) return already;
  const job = fetchOne(r, p.kind, p.id);
  inflight.set(r, job);
  try {
    return await job;
  } finally {
    inflight.delete(r);
  }
}

async function fetchOne(r: string, kind: Kind, id: number): Promise<string | null> {
  loading = r;
  sync();
  try {
    const url = URL.createObjectURL(await (loader as Loader)(kind, id));
    remember(r, url);
    return url;
  } catch (e) {
    // 404 = «ещё не озвучено»: помечаем навсегда, кнопка честно говорит «озвучки нет».
    // Прочее (сеть мигнула) — молча, при следующем нажатии попробуем снова.
    if ((e as { status?: number }).status === 404) gone.add(r);
    return null;
  } finally {
    if (loading === r) loading = null;
    sync();
  }
}

/** Запустить озвучку. Прежняя гаснет: объект Audio один на приложение. */
export async function play(r: string | null): Promise<void> {
  if (!r || current.mode === "off" || typeof Audio === "undefined") return;
  const mine = ++token;
  stop();
  const url = await grab(r);
  if (!url || mine !== token) { sync(); return; }
  const a = ensureEl();
  a.src = url;
  // ⭐⭐ Окно 1 запуска: скорость решает не только настройка, но и чередование — «повторное
  // проигрывание этой записи медленное». Считаем ДО старта, отмечаем ПОСЛЕ (не прозвучало —
  // не считается, иначе запрет автозапуска сдвинул бы чередование вхолостую).
  applyPitch(a, speedForNext(r));
  try {
    await a.play();
    // ⚠ «играет» ставим ПОСЛЕ фактического старта: событие `pause` от предыдущей записи
    // приходит отдельной задачей и, выставь мы состояние заранее, снимало бы его.
    if (mine !== token) return;
    notePlayed(r);
    playing = r;
    unlocked = true;
    blocked = false;
  } catch {
    // Не пустили (нет разрешения на автозапуск) — не врём молчанием.
    if (playing === r) playing = null;
    blocked = true;
  }
  sync();
}

/** Проговорить само — если это вообще разрешено настройкой и браузером. */
export function speak(pl: Places): boolean {
  const r = autoplayRef(pl);
  if (!r) return false;
  void play(r);
  return true;
}

export function stop(): void {
  playing = null;
  if (!el) return;
  try { el.pause(); el.currentTime = 0; } catch { /* не все клиенты умеют перемотку */ }
  sync();
}

/**
 * ⭐ Окно М2: «есть ли чем задать вопрос звуком». Ступень 1а держится на файле —
 * если его нет, карточка осталась бы без вопроса вовсе, поэтому экран занятия
 * спрашивает ЗАРАНЕЕ и молча опускается на ступень ниже. Тянем тем же путём, что и
 * кнопка: попал в кеш — ответ мгновенный, 404 — запомнили и больше не просим.
 */
export async function ensure(r: string | null): Promise<boolean> {
  if (!r || current.mode === "off" || !loader) return false;
  return !!(await grab(r));
}

/** Про этот файл уже известно, что его нет (404): спрашивать бесполезно. */
export function missing(r: string | null): boolean {
  return !!r && gone.has(r);
}

/** Тянем заранее, ничего не проигрывая (ошибки не мешают занятию). */
export function preload(refs: string[]): void {
  if (!loader || current.mode === "off") return;
  for (const r of refs) {
    if (urls.has(r) || gone.has(r) || inflight.has(r)) continue;
    void grab(r);
  }
}

/** Ушли с занятия: гасим звук и отзываем ВСЕ blob-адреса (иначе память их держит). */
export function release(): void {
  stop();
  for (const url of urls.values()) URL.revokeObjectURL(url);
  urls.clear();
  inflight.clear();
  token++;
  loading = null;
  resetSpeedCycle();
}

/**
 * Состояния кнопок прямо в DOM: ждёт (без класса) · тянется (`busy`) · играет (`on`) ·
 * озвучки нет (`none`) · ⭐ следующее нажатие медленное (`slow`). Через перерисовку экрана
 * этого делать нельзя: в «Контексте» перерисовка сбросила бы фокус и клавиатуру телефона.
 *
 * ⭐⭐ Окно 1 запуска: класс `slow` — обязательное требование владельца. Без видимой метки
 * человек не поймёт, ПОЧЕМУ звук вдруг изменился, и решит, что приложение сломалось.
 * Иконку по этому классу подменяет CSS (`styles/session.css`), подпись кнопки не трогаем:
 * на «На слух» её ведёт своя логика («Прослушать» → «Ещё раз»), и две руки в одном месте
 * уже однажды подрались.
 */
export function sync(): void {
  if (typeof document === "undefined") return;
  // Строго кнопки плеера (`button.snd`): чужой элемент с похожей меткой не должен ловить
  // наши классы и терять свой `aria-pressed` — на этом уже один раз поймали себя.
  document.querySelectorAll<HTMLElement>("button.snd[data-snd]").forEach((b) => {
    const r = b.dataset.snd || "";
    const missing = gone.has(r);
    const slow = !missing && slowNext(r);
    b.classList.toggle("on", r === playing);
    b.classList.toggle("busy", r === loading);
    b.classList.toggle("none", missing);
    b.classList.toggle("slow", slow);
    b.setAttribute("aria-pressed", String(r === playing));
    if (missing) b.setAttribute("title", "озвучки нет");
    else if (slow) b.setAttribute("title", "нажми — прозвучит медленно");
    else b.removeAttribute("title");
  });
}
